import { useContext, useEffect } from 'react';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import UserApi from '../../../rest-api/user-api';

export function useGenerateLink(handoutData, isBundled = false, whiteboardData = null) {
    const { authStore, userDataStore } = useContext(StoreContext);

    useEffect(() => {
        console.warn('Whiteboard data received before saving:', whiteboardData);
        if (whiteboardData) {
            console.log('=== Setting localStorage with whiteboard data');
            localStorage.setItem(
                'lastWhiteboardState',
                JSON.stringify({
                    whiteboardId: whiteboardData.whiteboardId,
                    handoutId: whiteboardData.handoutId,
                    canvasImageLink: whiteboardData.canvasImageLink,
                    canvasJsonLink: whiteboardData.canvasJsonLink,
                    fileName: whiteboardData.fileName || '',
                    eTag: whiteboardData.eTag || null,
                    ...(whiteboardData.updatedByFirstName && {
                        updatedByFirstName: whiteboardData.updatedByFirstName,
                    }),
                    ...(whiteboardData.updatedByLastName && {
                        updatedByLastName: whiteboardData.updatedByLastName,
                    }),
                    ...(whiteboardData.diagramVariant && { diagramVariant: whiteboardData.diagramVariant }),
                }),
            );
        }
    }, [whiteboardData]);

    // Process handout data to ensure it is in the correct format for the API call
    const processHandoutData = () => (isBundled ? handoutData.map((data) => data.contentId) : [handoutData.contentId]);

    const generateLink = async () => {
        const contentIds = processHandoutData();
        console.warn('Content IDs to process:', contentIds);

        // Re-read the latest saved whiteboard data from localStorage
        const lastWhiteboardState = localStorage.getItem('lastWhiteboardState');
        const latestWhiteboardData = lastWhiteboardState ? JSON.parse(lastWhiteboardState) : null;

        // Decide the type: only use whiteboard type if the toggle is on and official data exists
        // (You might also check a toggle flag from your state if available.)
        const type = latestWhiteboardData ? 'whiteboard' : 'handout';

        try {
            console.warn(
                'Whiteboard data being sent:',
                contentIds,
                type,
                latestWhiteboardData?.whiteboardId || null,
                latestWhiteboardData?.canvasImageLink || null,
                latestWhiteboardData?.eTag || null,
            );

            const result = await UserApi.generatePatientLink(
                authStore.idToken,
                contentIds,
                type,
                latestWhiteboardData?.whiteboardId || null,
                latestWhiteboardData?.canvasImageLink || null,
                latestWhiteboardData?.eTag || null,
                latestWhiteboardData?.updatedByFirstName || null,
                latestWhiteboardData?.updatedByLastName || null,
            );
            const linkId = result.linkId;
            const userId = userDataStore.userId;
            const linkWithClinicId = `${linkId}&clinicId=${userId}`;

            const dataToCache = {
                linkId,
                linkWithClinicId,
                whiteboardId: latestWhiteboardData ? latestWhiteboardData.whiteboardId : null,
                timestamp: new Date().getTime(),
            };
            localStorage.setItem('contentLink', JSON.stringify(dataToCache));

            return linkId; // returning the UUID, the name if the function is confusing (as this is "Only")
        } catch (error) {
            console.error('Error generating link:', error);
            throw new Error('Failed to generate link');
        }
    };

    const generateLinkOnly = async (toggleStates = {}) => {
        const linkId = await generateLink();

        const userId = userDataStore.userId;
        const url = new URL(process.env.REACT_APP_PATIENT_HANDOUT_URL);

        url.searchParams.append('linkId', linkId);
        url.searchParams.append('clinicId', userId);

        // If toggles are off, add them to the URL
        const { show3DModel, showMainIllustration, showTutorialVideo } = toggleStates;
        if (show3DModel === false) url.searchParams.append('show3DModel', 'false');
        if (showMainIllustration === false) url.searchParams.append('showMainIllustration', 'false');
        if (showTutorialVideo === false) url.searchParams.append('showTutorialVideo', 'false');

        return url.toString();
    };

    const createShareData = async (toggleStates = {}) => {
        const link = await generateLinkOnly(toggleStates);
        const title = `Cetra Health Information Sheet(s)`;
        const handoutTitle = handoutData.contentName;

        let messageSinglePhysio = `Please read your ${handoutTitle} handout by clicking the link below. This handout contains valuable information around your condition. If you have any questions about your condition, please don't hesitate to reach out directly - We're here to help!\n\n ${link}`;

        let messageBundledPhysio = `As discussed in your consultation, these handouts contain valuable information around your condition. \n\n Please take a moment to open and read the attached handouts. If you have any questions about your condition, please don't hesitate to reach out directly to us - We're here to help! \n\n ${link}`;
        //TODO: needs removal of not neccessery things
        let messageSingle = messageSinglePhysio;
        let messageBundled = messageBundledPhysio;

        const message = isBundled ? messageBundled : messageSingle;

        return { title, url: link, message };
    };

    return { generateLink, generateLinkOnly, createShareData };
}
